import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    Output,
} from '@angular/core';
import { IonicModule } from '@ionic/angular';

import { languageService, languageSupport, viewService } from '@unpispas/upp-base';
import type { Pinpad, PlaceView } from '@unpispas/upp-data';
import { UppWdgtModule } from '@unpispas/upp-wdgt';

/** Row for the TPV terminal picker (till label + ptf presence). */
export type ChargeCandidate = {
    terminal: Pinpad;
    tillName: string | null;
    connected: boolean;
};

/**
 * Embedded TPV picker panel: choose which Paytef terminal receives a sale or loose refund
 * when the place has more than one active terminal.
 * Hosted in `upp-dialog` by ticket edit/thumb — shown/hidden with the host `*ngIf`.
 * Selector is `upp-pinpad-select` to avoid colliding with `upp-wdgt` `upp-select`.
 */
@Component({
    selector: 'upp-pinpad-select',
    standalone: true,
    imports: [CommonModule, IonicModule, UppWdgtModule],
    templateUrl: './upp-select.html',
    styleUrls: ['./upp-select.scss'],
})
export class UppPinpadSelectComponent extends languageSupport {
    @Input() options: ChargeCandidate[] = [];
    @Input() selected: Pinpad | null = null;
    @Output() OnPick = new EventEmitter<Pinpad>();
    @Output() OnCancel = new EventEmitter<void>();

    constructor(
        lang: languageService,
        change: ChangeDetectorRef,
        public view: viewService,
    ) {
        super(lang, change);
    }

    /**
     * Builds picker rows from place inventory (till label + connected badge).
     * Hosts resolve 0/1 terminals via `place.pinpads`; call this only when opening the picker.
     */
    static BuildOptions(place: PlaceView): ChargeCandidate[] {
        const _rows: ChargeCandidate[] = [];
        for (const _terminal of place.pinpads) {
            const _till = _terminal.rasppi?.till ?? null;
            _rows.push({
                terminal: _terminal,
                tillName: _till?.name ?? null,
                connected: _terminal.Connected,
            });
        }

        _rows.sort((left, right) => {
            const _tillCmp = (left.tillName ?? '').localeCompare(right.tillName ?? '');
            if (_tillCmp !== 0) {
                return _tillCmp;
            }
            return (left.terminal.name ?? '').localeCompare(right.terminal.name ?? '');
        });

        return _rows;
    }

    OptionTrack(_index: number, option: ChargeCandidate): string | null {
        return option.terminal?._uuid || null;
    }

    IsSelected(option: ChargeCandidate): boolean {
        return option.terminal === this.selected;
    }

    TillLabel(option: ChargeCandidate): string {
        return option.tillName?.trim()
            ? option.tillName
            : this.tr('@ticket_term_pick_no_till');
    }

    OnOptionClick(option: ChargeCandidate): void {
        this.OnPick.emit(option.terminal);
    }

    OnCancelClick(): void {
        this.OnCancel.emit();
    }
}
